
import React, { useMemo, useState } from 'react';
import { useLeads, LeadStageType, ServiceType } from '../context/LeadContext';
import { usePartners } from '../context/PartnerContext';
import LeadCard from '../components/leads/LeadCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { isToday, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LeadReassignment } from '@/components/leads/LeadReassignment';
import { PageHeader } from '@/components/layouts/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/use-toast';
import {
  Search,
  UserRound,
  Plus,
  Filter,
  CalendarDays,
  Clock,
  CheckCircle,
  Info,
  Sparkles,
  ArrowUpRight,
  Eye,
  Loader2,
  Trash2,
  AlertTriangle,
  X,
  Globe,
  Megaphone,
} from 'lucide-react';

export default function Leads() {
  const { leads, deleteLead } = useLeads();
  const { partners } = usePartners();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<LeadStageType | 'all'>('all');
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>('all');
  const [queueFilter, setQueueFilter] = useState<'all' | 'unassigned' | 'today' | 'stale'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'facebook_sheets' | 'facebook_leads' | 'web'>('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const leadsStats = useMemo(() => {
    const total = leads.length;
    const newLeads = leads.filter(l => l.stage === 'Νέο').length;
    const pendingLeads = leads.filter(l => l.stage === 'σεΕκκρεμότητα').length;
    const successLeads = leads.filter(l => l.stage === 'έκλεισεΗΔουλειά').length;
    const infoLeads = leads.filter(l => l.stage === 'ήθελεΠληροφορίες').length;
    const unassigned = leads.filter(l => !l.partnerId).length;
    const todayLeads = leads.filter(l => isToday(parseISO(l.createdAt))).length;
    const stalePending = leads.filter(l => {
      if (l.stage !== 'σεΕκκρεμότητα') return false;
      const ageHours = (Date.now() - parseISO(l.createdAt).getTime()) / (1000 * 60 * 60);
      return ageHours >= 48;
    }).length;
    const assigned = total - unassigned;
    const assignmentCoverage = total > 0 ? Math.round((assigned / total) * 100) : 0;
    const successRate = total > 0 ? Math.round((successLeads / total) * 100) : 0;

    return { total, newLeads, pendingLeads, successLeads, infoLeads, unassigned, stalePending, todayLeads, assigned, assignmentCoverage, successRate };
  }, [leads]);

  const uniqueCampaigns = useMemo(
    () => [...new Set(leads.map(l => l.campaign).filter(Boolean) as string[])].sort(),
    [leads]
  );

  const filteredLeads = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    let filtered = leads.filter(lead => {
      if (!normalizedSearch) return true;
      return (
        lead.name.toLowerCase().includes(normalizedSearch) ||
        lead.phone.includes(normalizedSearch) ||
        lead.address.toLowerCase().includes(normalizedSearch) ||
        lead.postalCode.toLowerCase().includes(normalizedSearch)
      );
    });
    if (stageFilter !== 'all') filtered = filtered.filter(l => l.stage === stageFilter);
    if (serviceFilter !== 'all') filtered = filtered.filter(l => l.service === serviceFilter);
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(l => {
        const s = l.source ?? 'web';
        return s === sourceFilter;
      });
    }
    if (campaignFilter !== 'all') filtered = filtered.filter(l => l.campaign === campaignFilter);
    if (queueFilter === 'unassigned') filtered = filtered.filter(l => !l.partnerId);
    if (queueFilter === 'today') filtered = filtered.filter(l => isToday(parseISO(l.createdAt)));
    if (queueFilter === 'stale') {
      filtered = filtered.filter(l => {
        if (l.stage !== 'σεΕκκρεμότητα') return false;
        const ageHours = (Date.now() - parseISO(l.createdAt).getTime()) / (1000 * 60 * 60);
        return ageHours >= 48;
      });
    }
    return [...filtered].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortBy === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }, [leads, queueFilter, search, serviceFilter, sourceFilter, campaignFilter, sortBy, stageFilter]);

  const isStale = (lead: typeof leads[0]) =>
    lead.stage === 'σεΕκκρεμότητα' &&
    (Date.now() - parseISO(lead.createdAt).getTime()) / 3_600_000 >= 48;

  const hasActiveFilters =
    search !== '' || stageFilter !== 'all' || serviceFilter !== 'all' || sourceFilter !== 'all' || campaignFilter !== 'all' || queueFilter !== 'all' || sortBy !== 'newest';

  const clearFilters = () => {
    setSearch('');
    setStageFilter('all');
    setServiceFilter('all');
    setSourceFilter('all');
    setCampaignFilter('all');
    setQueueFilter('all');
    setSortBy('newest');
  };

  const handleDeleteClick = (leadId: string) => {
    setLeadToDelete(leadId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leadToDelete) return;
    try {
      setIsDeleting(true);
      await deleteLead(leadToDelete);
      toast({ title: 'Επιτυχής διαγραφή', description: 'Το lead διαγράφηκε επιτυχώς.' });
      setShowDeleteDialog(false);
      setLeadToDelete(null);
    } catch (error) {
      toast({
        title: 'Αποτυχία διαγραφής',
        description: error instanceof Error ? error.message : 'Υπήρξε πρόβλημα κατά τη διαγραφή.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Quick filter tab config
  const queueTabs: { key: typeof queueFilter; label: string; count: number; alert?: boolean }[] = [
    { key: 'all',        label: 'Όλα',                 count: leadsStats.total },
    { key: 'unassigned', label: 'Χωρίς ανάθεση',       count: leadsStats.unassigned, alert: leadsStats.unassigned > 0 },
    { key: 'today',      label: 'Σήμερα',               count: leadsStats.todayLeads },
    { key: 'stale',      label: 'Εκκρεμότητα 48h+',    count: leadsStats.stalePending, alert: leadsStats.stalePending > 0 },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Ενδιαφερόμενοι"
        description="Διαχείριση επαφών και leads"
        icon={<UserRound />}
        iconGradient="purple"
        actions={
          <>
            <Link to="/admin/leads/statistics">
              <Button variant="outline" className="border-slate-200">
                <ArrowUpRight className="mr-2 h-4 w-4" /> Στατιστικά
              </Button>
            </Link>
            <Link to="/admin/leads/assignment">
              <Button variant="gradient">
                <Plus className="mr-2 h-4 w-4" /> Ανάθεση Leads
              </Button>
            </Link>
          </>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={leadsStats.newLeads}
          title="Νέα"
          icon={<Sparkles />}
          variant="info"
          size="compact"
        />
        <StatCard
          value={leadsStats.pendingLeads}
          title="Σε Αναμονή"
          icon={<Clock />}
          variant="warning"
          size="compact"
        />
        <StatCard
          value={leadsStats.successLeads}
          title="Επιτυχίες"
          icon={<CheckCircle />}
          variant="success"
          size="compact"
        />
        <StatCard
          value={leadsStats.infoLeads}
          title="Πληροφορίες"
          icon={<Info />}
          variant="gradient"
          gradientColors="from-slate-500 to-slate-600"
          size="compact"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Leads List — 3 cols */}
        <div className="xl:col-span-3 space-y-4">
          {/* Filters card */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              {/* Search + selects */}
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Αναζήτηση ονόματος, τηλεφώνου, διεύθυνσης, Τ.Κ..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select
                  value={stageFilter}
                  onValueChange={(v) => setStageFilter(v as LeadStageType | 'all')}
                >
                  <SelectTrigger className="w-full md:w-48 bg-slate-50 border-slate-200">
                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Στάδιο" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλα τα Στάδια</SelectItem>
                    <SelectItem value="Νέο">Νέο</SelectItem>
                    <SelectItem value="σεΕκκρεμότητα">Σε Εκκρεμότητα</SelectItem>
                    <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η Δουλειά</SelectItem>
                    <SelectItem value="ήθελεΠληροφορίες">Ήθελε Πληροφορίες</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={serviceFilter}
                  onValueChange={(v) => setServiceFilter(v as ServiceType | 'all')}
                >
                  <SelectTrigger className="w-full md:w-48 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Υπηρεσία" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες οι Υπηρεσίες</SelectItem>
                    <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                    <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                    <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                    <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                    <SelectItem value="φυσικόΑέριο">Φυσικό Αέριο</SelectItem>
                    <SelectItem value="ηλιακά">Ηλιακά</SelectItem>
                    <SelectItem value="άλλο">Άλλο</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'newest' | 'oldest')}>
                  <SelectTrigger className="w-full md:w-44 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Ταξινόμηση" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Νεότερα πρώτα</SelectItem>
                    <SelectItem value="oldest">Παλαιότερα πρώτα</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Source + Campaign filters */}
              <div className="flex flex-col md:flex-row gap-3">
                <Select
                  value={sourceFilter}
                  onValueChange={(v) => setSourceFilter(v as typeof sourceFilter)}
                >
                  <SelectTrigger className="w-full md:w-52 bg-slate-50 border-slate-200">
                    <Globe className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Πηγή" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες οι Πηγές</SelectItem>
                    <SelectItem value="facebook_sheets">Facebook Sheets</SelectItem>
                    <SelectItem value="facebook_leads">Facebook Ads</SelectItem>
                    <SelectItem value="web">Web Form</SelectItem>
                  </SelectContent>
                </Select>
                {uniqueCampaigns.length > 0 && (
                  <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                    <SelectTrigger className="w-full md:w-64 bg-slate-50 border-slate-200">
                      <Megaphone className="h-4 w-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Καμπάνια" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Όλες οι Καμπάνιες</SelectItem>
                      {uniqueCampaigns.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Queue tabs */}
              <div className="flex flex-wrap gap-2">
                {queueTabs.map(({ key, label, count, alert }) => (
                  <button
                    key={key}
                    onClick={() => setQueueFilter(key)}
                    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                      queueFilter === key
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {alert && <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />}
                    <CalendarDays className={`h-3.5 w-3.5 ${key === 'today' ? '' : 'hidden'}`} />
                    {label}
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                        queueFilter === key
                          ? 'bg-white/20 text-white'
                          : alert
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-white text-slate-600'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Results bar */}
          <div className="flex items-center justify-between px-1">
            <p className="text-sm text-slate-500">
              Εμφάνιση{' '}
              <span className="font-semibold text-slate-700">{filteredLeads.length}</span>
              {' '}από{' '}
              <span className="font-semibold text-slate-700">{leadsStats.total}</span>
              {' '}επαφές
            </p>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-blue-600 hover:text-blue-700 gap-1.5 h-7"
              >
                <X className="h-3.5 w-3.5" />
                Καθαρισμός
              </Button>
            )}
          </div>

          {/* Leads list */}
          {filteredLeads.length === 0 ? (
            <EmptyState
              icon={<UserRound />}
              title="Δεν βρέθηκαν επαφές"
              description={hasActiveFilters ? 'Δοκιμάστε διαφορετικά φίλτρα.' : 'Δεν υπάρχουν ακόμα leads.'}
              action={hasActiveFilters ? <Button variant="outline" onClick={clearFilters}>Καθαρισμός φίλτρων</Button> : undefined}
            />
          ) : (
            <div className="space-y-2.5">
              {filteredLeads.map((lead, index) => (
                <div
                  key={lead.id}
                  className={`animate-slide-up rounded-xl overflow-hidden transition-all ${
                    isStale(lead)
                      ? 'ring-1 ring-rose-200 shadow-sm shadow-rose-100'
                      : ''
                  }`}
                  style={{ animationDelay: `${index * 25}ms` }}
                >
                  {isStale(lead) && (
                    <div className="flex items-center gap-1.5 bg-rose-50 px-3 py-1.5 border-b border-rose-100">
                      <AlertTriangle className="h-3.5 w-3.5 text-rose-600 shrink-0" />
                      <span className="text-xs font-medium text-rose-700">Χρειάζεται follow-up — εκκρεμεί &gt; 48 ώρες</span>
                    </div>
                  )}
                  <LeadCard lead={lead} onSendSMS={() => {}}>
                    <Link to={`/lead/${lead.id}`}>
                      <Button
                        size="sm"
                        className="h-9 gap-2 bg-slate-900 hover:bg-slate-800"
                        aria-label={`Προβολή λεπτομερειών για ${lead.name}`}
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden sm:inline">Προβολή</span>
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(lead.id)}
                      className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={`Διαγραφή lead ${lead.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </LeadCard>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar — 1 col */}
        <div className="xl:col-span-1 space-y-4">
          {/* Assignment coverage */}
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold text-slate-700">Κατάσταση Ανάθεσης</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Coverage bar */}
              <div>
                <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                  <span>Coverage ανάθεσης</span>
                  <span className="font-semibold text-slate-800">{leadsStats.assignmentCoverage}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      leadsStats.assignmentCoverage >= 80
                        ? 'bg-emerald-500'
                        : leadsStats.assignmentCoverage >= 50
                        ? 'bg-amber-500'
                        : 'bg-rose-500'
                    }`}
                    style={{ width: `${leadsStats.assignmentCoverage}%` }}
                  />
                </div>
              </div>

              {/* Mini stats grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
                  <p className="text-xl font-bold text-slate-800">{leadsStats.assigned}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Ανατεθειμένα</p>
                </div>
                <div className={`rounded-lg border p-3 text-center ${leadsStats.unassigned > 0 ? 'bg-amber-50 border-amber-100' : 'bg-slate-50 border-slate-100'}`}>
                  <p className={`text-xl font-bold ${leadsStats.unassigned > 0 ? 'text-amber-700' : 'text-slate-800'}`}>{leadsStats.unassigned}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Χωρίς ανάθεση</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-center">
                  <p className="text-xl font-bold text-slate-800">{partners.filter(p => p.active).length}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Ενεργοί συν/τες</p>
                </div>
                <div className={`rounded-lg border p-3 text-center ${leadsStats.stalePending > 0 ? 'bg-rose-50 border-rose-100' : 'bg-slate-50 border-slate-100'}`}>
                  <p className={`text-xl font-bold ${leadsStats.stalePending > 0 ? 'text-rose-700' : 'text-slate-800'}`}>{leadsStats.stalePending}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Εκκρ. 48h+</p>
                </div>
              </div>

              {/* Success rate */}
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-emerald-700 font-medium">Ποσοστό επιτυχίας</p>
                  <p className="text-lg font-bold text-emerald-700">{leadsStats.successRate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <LeadReassignment />
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Lead</DialogTitle>
            <DialogDescription>
              Είσαι σίγουρος ότι θέλεις να διαγράψεις αυτό το lead; Η ενέργεια δεν αναιρείται.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Άκυρο
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Διαγραφή...</>
              ) : (
                'Διαγραφή'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
